/**
 * Recommendations link to search results in prose. Show those results in the order
 * they are recommended, followed by the remaining results in search ranking order.
 */

const MARKDOWN_LINK_TARGET = /\]\(([^)\s]+)/g;

/**
 * Extracts the URL target of every markdown link in the prose. Handles optional link titles
 * (`[text](url "title")`) by stopping at the first whitespace.
 */
export function extractMentionedUrls(content: string): Set<string> {
    const urls = new Set<string>();
    for (const match of content.matchAll(MARKDOWN_LINK_TARGET)) {
        urls.add(match[1]);
    }
    return urls;
}

/**
 * Returns a new list with results whose URL appears in the prose moved to the front, in
 * first-mention order; the rest follow in original order. Returns the input unchanged
 * when there is no prose or nothing matches, so non-tool-suggestion callers are unaffected.
 */
export function reorderResultsByMention<T extends {url: string}>(
    results: T[],
    messageContent?: string
): T[] {
    if (!messageContent) return results;

    const mentioned = extractMentionedUrls(messageContent);
    if (mentioned.size === 0) return results;

    const named: T[] = [];
    const rest: T[] = [];
    for (const result of results) {
        if (result.url && mentioned.has(result.url)) {
            named.push(result);
        } else {
            rest.push(result);
        }
    }

    const mentionOrder = new Map([...mentioned].map((url, index) => [url, index]));
    named.sort((a, b) => mentionOrder.get(a.url)! - mentionOrder.get(b.url)!);
    return named.length > 0 ? [...named, ...rest] : results;
}
